// drawArgbCanvas — 旧 Java applet `ColorMakeCanvas.java` (RGB / HSB 両モード) の
// paint ロジックを 1:1 で TS port した pure draw function. node 環境で context2d を
// minimal mock してテスト可能にするため React に依存しない形で切り出す.
//
// Phase 6.7 サブ task 後追い (consult 2026-05-07): 旧 `ColorMakeCanvas.java:467-490`
// の Meta+click トグル (ARGB ⇄ HSB) を新版に移植. 同じ 4 本スライダ UI のラベルと
// バー塗りグラデーションが 'A/R/G/B' と 'A/H/S/B' で切り替わる. Alpha バーは
// 両モードで完全に同じ (= alpha 値は色空間と独立).
//
// 2026-05-31 layer-popup-visibility-toggle (`docs/_wip/w3-classic-layers.md §2.2.1`):
//   旧版踏襲の下部 10px ラベル領域 (= 'A/R/G/B' 表示) は ArgbSliderPanel 側で
//   label-as-button (= `[A R G B]` / `[A H S V]` toggle) に移譲したため撤去.
//   canvas 縦寸法は 74 → 64 (= バー領域だけ) に縮め、空いた h-5 を Layer toggle
//   button に再利用する. canvas 内部の channel labels (fillText('A'/'R'/...))
//   と下部 gray strip は本ファイルからまるごと削る (= 描画関数が「バー領域のみ」
//   に純化される、UI 統合の局所性を上げる).
//
//   ARGB_BAR_AREA_HEIGHT は 64 のまま維持 (= 内部寸法 / y→value 計算の基準として
//   不変). ARGB_STR_X は新 toggle button の文字配置との視覚的一致を取りやすいよう
//   export を維持 (= 旧 channel labels の x 座標 4/16/28/40 を toggle button の
//   等幅 spacing に流用できる、内部で未使用になるが API 互換).
//
// 旧版踏襲のレイアウト確定値 (出典: ColorMakeCanvas.java:23-43, 100-128, 198-323):
//   DSP_W = 50, DSP_H = 64 (= 旧 74 から 10px ラベル領域を 2026-05-31 で撤去)
//   0..63 (= 64px 高) がバー領域 (背景: white)
//
//   バー位置 (透過度ありモード = ARGB):
//     A: bar_x=2,  str_x=4   (10px 幅, 64px 高)
//     R: bar_x=14, str_x=16
//     G: bar_x=26, str_x=28
//     B: bar_x=38, str_x=40
//
//   バー描画: 旧版は `g.fillRect(bar_x, 64-(value/4), 10, value/4)` で
//             下から上に value/4 px 塗りつぶす "ゲージ表示" 方式 (slider thumb
//             ではない). 64px 高に対し value 0..255 が 0..64 px に相当.
//
//   目盛: lightGray で 8px 間隔の横線 (旧版 ColorMakeCanvas.java:200-204
//         `for i = 0..5: g.drawRect(0, i*8, 64, i*8)` ≒ 7 本の横線).
//
//   hex 値 (バー上 y=60 = `64-4`):
//     2 桁 hex 大文字 (ColorMakeCanvas.java:290-320).
//     文字色: A=黒 / R=#ffb0b0 / G=#008000 / B=#b0b0ff (Java の Color 値
//             new Color(0x0ff,0x0b0,0x0b0) 等を素直に hex 化).
//     font: `Dialog Plain 8px` → Web Canvas では `8px sans-serif` で代替.
//
// A バー塗り色 (旧版踏襲, ColorMakeCanvas.java:198-211):
//   旧 Java では `g.setColor(Color.lightGray)` の直後に目盛を描き, RGB モードの
//   A バー塗り部分も `setColor` を挟まず lightGray のまま fillRect している。
//   結果として A バーの塗り色は **#c0c0c0 (lightGray)** になる。R/G/B バーは
//   `setColor(Color.red/green/blue)` で塗り直すため #ff0000 / #00ff00 / #0000ff。
//   Web 移植もこれを完全踏襲し, checker 背景等の独自演出は撒かない (ユーザ指示)。

/** Canvas 全体サイズ (旧版 DSP_W / DSP_H. 2026-05-31 で旧 DSP_H=74 から 10px ラベル領域を撤去). */
export const ARGB_CANVAS_WIDTH = 50;
/**
 * Canvas 縦寸法 (= バー領域のみ).
 *
 * 2026-05-31 layer-popup-visibility-toggle: 旧 74 (= バー 64 + ラベル 10) から
 * 64 に縮小. 下部 channel labels (`A/R/G/B` / `A/H/S/V`) を `ArgbSliderPanel` 内の
 * label-as-button (= `[A R G B]` / `[A H S V]` toggle) に移譲したため、canvas 内に
 * ラベル領域を持つ必要が無くなった (`docs/_wip/w3-classic-layers.md §2.2.1`).
 *
 * `ARGB_BAR_AREA_HEIGHT` (= 64) と完全一致 (= バー領域 = canvas 全体).
 */
export const ARGB_CANVAS_HEIGHT = 64;
/** バー領域の高さ. 2026-05-31 以降は ARGB_CANVAS_HEIGHT と一致 (= 下部ラベル領域撤去). */
export const ARGB_BAR_AREA_HEIGHT = 64;
export const ARGB_BAR_WIDTH = 10;

/** 各 channel のバー左上 x 座標 (旧版 BARA_*_X). */
export const ARGB_BAR_X = {
  a: 2,
  r: 14,
  g: 26,
  b: 38,
} as const;

/** 各 channel のラベル文字列 x 座標 (旧版 STRA_*_X). */
export const ARGB_STR_X = {
  a: 4,
  r: 16,
  g: 28,
  b: 40,
} as const;

export type ArgbChannel = 'a' | 'r' | 'g' | 'b';
/** HSB モード時の channel ラベル (旧版踏襲: A は両モード共通). */
export type HsbChannel = 'a' | 'h' | 's' | 'v';
/** 描画モード. 'argb' = 旧版 RGB モード / 'hsb' = 旧版 HSB モード (Meta+click toggle). */
export type ArgbMode = 'argb' | 'hsb';

export interface ArgbValues {
  readonly valueA: number;
  readonly valueR: number;
  readonly valueG: number;
  readonly valueB: number;
}

/**
 * HSB モード用の値. すべて 0..255 正規化済 (旧 Java `(hsb[*] * 255)` 踏襲).
 * 内部統一スケールを ARGB と揃えることで `yToValue` 等の helper が共通化できる.
 */
export interface HsbValues {
  readonly valueA: number;
  readonly valueH: number;
  readonly valueS: number;
  readonly valueV: number;
}

/** Channel ごとの hex 値表示色 (旧版 ColorMakeCanvas.java:311-320 完全踏襲). */
const HEX_TEXT_COLOR: Record<ArgbChannel, string> = {
  a: '#000000',
  r: '#ffb0b0',
  g: '#008000',
  b: '#b0b0ff',
};

/** Channel ごとのバー塗り色 (旧版踏襲: lightGray / 赤 / 緑 / 青). */
const BAR_FILL_COLOR: Record<ArgbChannel, string> = {
  // 旧版 ColorMakeCanvas.java:198-211 で A バーは目盛 lightGray を引き継いだまま塗る。
  a: '#c0c0c0',
  r: '#ff0000',
  g: '#00ff00',
  b: '#0000ff',
};

/**
 * HSB モードの hex 値表示色. 旧版 Java は ARGB ⇄ HSB トグル時にも文字色を変えなかった
 * (= R/G/B の文字色をそのまま継承) が、新版では役割名 (H=Hue, S=Sat, V=Val) を
 * 視認できるよう若干区別する: H は色相環全色を匂わせるためマゼンタ寄り、S は無彩色から
 * 鮮やかへの軸でターコイズ寄り、V は黒〜白の軸で中間グレー寄り. UI 視認性向上目的の
 * 独自対応 (旧版にはなかったが、機能実装担当の判断で付与可能と consult 仕様で確定).
 */
const HSB_HEX_TEXT_COLOR: Record<HsbChannel, string> = {
  a: '#000000',
  h: '#ff80ff',
  s: '#80ffff',
  v: '#c0c0c0',
};

/** 0..255 → 2 桁 uppercase hex. */
const toHex2 = (v: number): string => {
  const clamped = Math.max(0, Math.min(255, Math.round(v)));
  return clamped.toString(16).padStart(2, '0').toUpperCase();
};

/**
 * Canvas y 座標 (0..63) → 0..255 値. 旧版 mouseDragged: `(64 - y) * 4`.
 * 範囲外は端で clamp.
 */
export const yToValue = (yCanvas: number): number => {
  const y = Math.max(0, Math.min(ARGB_BAR_AREA_HEIGHT, yCanvas));
  return Math.max(0, Math.min(255, Math.round((ARGB_BAR_AREA_HEIGHT - y) * 4)));
};

/**
 * Canvas x 座標 → どの channel のバーか判定. バー幅 10px の hit 判定.
 * 該当しない場合は null.
 */
export const xToChannel = (xCanvas: number): ArgbChannel | null => {
  for (const ch of ['a', 'r', 'g', 'b'] as const) {
    const bx = ARGB_BAR_X[ch];
    if (xCanvas >= bx && xCanvas < bx + ARGB_BAR_WIDTH) return ch;
  }
  return null;
};

/**
 * Canvas x 座標 → HSB モード時の channel (a/h/s/v). バー位置は ARGB と完全同一なので,
 * `xToChannel` の戻り値を rename mapping するだけ. 'a' はそのまま.
 */
export const xToHsbChannel = (xCanvas: number): HsbChannel | null => {
  const ch = xToChannel(xCanvas);
  if (ch === null) return null;
  // バー位置 1:1 mapping. R→H / G→S / B→V (旧 ColorMakeCanvas.java:467-490 の
  // valueR/valueG/valueB に HSB 値を代入する流儀を踏襲).
  if (ch === 'a') return 'a';
  if (ch === 'r') return 'h';
  if (ch === 'g') return 's';
  return 'v';
};

/**
 * 旧 ColorMakeCanvas.java の `clear()` + `drawValues()` 相当を 1 関数で実装.
 * 描画順は旧版踏襲: 背景 → 目盛 → バー → hex 値 → ラベル.
 *
 * Phase 6.7 サブ task 後追い (2026-05-07): mode 省略時は `'argb'` (= 後方互換).
 * `mode='hsb'` を渡すと旧 Meta+click 後の HSB 表示にトグルする (= バー塗りが
 * H 全色相 / S 0→鮮やか / V 黒→明 のグラデーション、ラベル A/H/S/B).
 *
 * @param ctx Canvas 2D context (50x74 を想定. resize は呼び出し側責務).
 * @param values 0..255 の各 channel value (ARGB = R/G/B / HSB = H/S/V).
 * @param mode `'argb'` | `'hsb'` (default `'argb'`).
 */
export const drawArgbCanvas = (
  ctx: CanvasRenderingContext2D,
  values: ArgbValues | HsbValues,
  mode: ArgbMode = 'argb',
): void => {
  // 1. 背景: 全面 white (両モード共通). 2026-05-31 layer-popup-visibility-toggle:
  //    旧 74px 時代の下部 10px gray strip (= channel labels 用の領域) は撤去済.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, ARGB_CANVAS_WIDTH, ARGB_BAR_AREA_HEIGHT);

  // 2. 目盛: lightGray で 8px ごとに横線 (上部バー領域全幅).
  //    旧版 ColorMakeCanvas.java:200-204 に倣い 7 本 (i=0..6) 描画 (両モード共通).
  ctx.fillStyle = '#c0c0c0';
  for (let i = 0; i <= 6; i++) {
    ctx.fillRect(0, i * 8, ARGB_CANVAS_WIDTH, 1);
  }

  // 3. バー描画はモードで分岐.
  if (mode === 'argb') {
    drawArgbBars(ctx, values as ArgbValues);
  } else {
    drawHsbBars(ctx, values as HsbValues);
  }

  // 4. hex 値 (バー上, y=60 = 64-4). font 8px sans-serif (Java Dialog Plain 8 代替).
  //    バー内部の値表示 (= スポイト的に何の色が乗っているかを数値で読む) は別役割なので維持.
  ctx.font = '8px sans-serif';
  ctx.textBaseline = 'alphabetic';
  if (mode === 'argb') {
    const v = values as ArgbValues;
    const drawHex = (ch: ArgbChannel, value: number): void => {
      ctx.fillStyle = HEX_TEXT_COLOR[ch];
      ctx.fillText(toHex2(value), ARGB_BAR_X[ch], ARGB_BAR_AREA_HEIGHT - 4);
    };
    drawHex('a', v.valueA);
    drawHex('r', v.valueR);
    drawHex('g', v.valueG);
    drawHex('b', v.valueB);
  } else {
    const v = values as HsbValues;
    // HSB モード: H=R 列, S=G 列, V=B 列 (バー位置完全同一).
    ctx.fillStyle = HSB_HEX_TEXT_COLOR.a;
    ctx.fillText(toHex2(v.valueA), ARGB_BAR_X.a, ARGB_BAR_AREA_HEIGHT - 4);
    ctx.fillStyle = HSB_HEX_TEXT_COLOR.h;
    ctx.fillText(toHex2(v.valueH), ARGB_BAR_X.r, ARGB_BAR_AREA_HEIGHT - 4);
    ctx.fillStyle = HSB_HEX_TEXT_COLOR.s;
    ctx.fillText(toHex2(v.valueS), ARGB_BAR_X.g, ARGB_BAR_AREA_HEIGHT - 4);
    ctx.fillStyle = HSB_HEX_TEXT_COLOR.v;
    ctx.fillText(toHex2(v.valueV), ARGB_BAR_X.b, ARGB_BAR_AREA_HEIGHT - 4);
  }

  // 5. 下部 channel labels (旧 y=72 の 'A/R/G/B' / 'A/H/S/V') は
  //    2026-05-31 layer-popup-visibility-toggle で撤去済. mode 表示は
  //    `ArgbSliderPanel` の label-as-button (= `[A R G B]` / `[A H S V]` toggle)
  //    に移譲. canvas は純粋にバー領域だけを描く (= ARGB エリア統合 §2.2.1).
};

/* ─── ARGB モード bar 描画 (旧 fillRect(bar_x, 64-(v/4), 10, v/4) 完全踏襲) ── */

const drawArgbBars = (ctx: CanvasRenderingContext2D, values: ArgbValues): void => {
  const drawBar = (ch: ArgbChannel, value: number): void => {
    const v = Math.max(0, Math.min(255, value));
    const fillH = Math.floor(v / 4); // 0..63
    if (fillH <= 0) return;
    ctx.fillStyle = BAR_FILL_COLOR[ch];
    ctx.fillRect(ARGB_BAR_X[ch], ARGB_BAR_AREA_HEIGHT - fillH, ARGB_BAR_WIDTH, fillH);
  };
  drawBar('a', values.valueA);
  drawBar('r', values.valueR);
  drawBar('g', values.valueG);
  drawBar('b', values.valueB);
};

/* ─── HSB モード bar 描画 ────────────────────────────────────────────────── */
//
// 旧 Java は HSB モードでもバー塗りは ARGB と同色 (= R 列が赤、G 列が緑) のまま
// だったが (`ColorMakeCanvas.java:198-298` を読む限り mode 別の分岐がない), 新版では
// 視認性向上のため H/S/V を意味するグラデーションに切替える. consult 2026-05-07
// で「実装担当判断」と確定済の独自対応.
//
//   - **A バー** (両モード共通): lightGray 単色 (= ARGB と完全同一描画、別ヘルパ
//     にせず drawArgbBars と同じ振る舞い).
//   - **H バー (Hue)**: 0..255 で全色相 (赤→黄→緑→シアン→青→マゼンタ→赤).
//     y=63 (下端) → hue=0、y=0 (上端) → hue=359 として 1px ずつ HSL→RGB 変換.
//   - **S バー (Saturation)**: 現在の H, V を固定して S 0→255 のグラデーション.
//     y=63 → S=0 (= 中明度のグレー or 白っぽい), y=0 → S=255 (= 鮮やか).
//   - **V バー (Brightness)**: 現在の H, S を固定して V 0→255 のグラデーション.
//     y=63 → V=0 (黒), y=0 → V=255 (= 現在 H/S の最も明るい色).
//
// グラデーションは fillRect で 64 本 (= 1px x 10px) 個別に描画する. canvas
// gradient API (createLinearGradient) を使えば 1 回で済むが, テストで
// fillRect/fillStyle 列を観察できる現行 fake ctx を流用するため fillRect ループ
// 方式を維持する.

/**
 * HSB バー領域内のローカル y (0..63) を 0..255 値に逆変換 (yToValue と同じ式).
 * y=0 (上端) → 255 / y=64 → 0 / y=32 → 128 (= 旧 (64-y)*4).
 */
const yLocalToValue255 = (yLocal: number): number => {
  return Math.max(0, Math.min(255, (ARGB_BAR_AREA_HEIGHT - yLocal) * 4));
};

/**
 * HSV (h: 0..360, s/v: 0..1) → CSS rgb 文字列. drawArgbCanvas 内部の HSB バー
 * グラデーション計算用の最小実装. 既存 colorWheel.ts の hsvaToRgba を import すると
 * pure draw の依存が増えて test mock の負荷が上がるため自前で持つ (= 入力レンジ
 * 限定の最小ロジック、std HSV→RGB 公式).
 */
const hsvToCssRgb = (h: number, s: number, v: number): string => {
  const c = v * s;
  const hh = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hh < 1) {
    r = c;
    g = x;
  } else if (hh < 2) {
    r = x;
    g = c;
  } else if (hh < 3) {
    g = c;
    b = x;
  } else if (hh < 4) {
    g = x;
    b = c;
  } else if (hh < 5) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  const m = v - c;
  const ri = Math.max(0, Math.min(255, Math.round((r + m) * 255)));
  const gi = Math.max(0, Math.min(255, Math.round((g + m) * 255)));
  const bi = Math.max(0, Math.min(255, Math.round((b + m) * 255)));
  return `rgb(${ri},${gi},${bi})`;
};

const drawHsbBars = (ctx: CanvasRenderingContext2D, values: HsbValues): void => {
  const { valueA, valueH, valueS, valueV } = values;

  // A バー: ARGB と完全同一 (lightGray ゲージ).
  const fillH = Math.floor(Math.max(0, Math.min(255, valueA)) / 4);
  if (fillH > 0) {
    ctx.fillStyle = BAR_FILL_COLOR.a;
    ctx.fillRect(ARGB_BAR_X.a, ARGB_BAR_AREA_HEIGHT - fillH, ARGB_BAR_WIDTH, fillH);
  }

  // H バー (R 列): 全色相 0..359 のグラデーション (現在の H 値とは独立に「全色相を提示」).
  // y=0 (上) → 360°, y=63 (下) → 0°. 1px ずつ fillRect で描く.
  for (let y = 0; y < ARGB_BAR_AREA_HEIGHT; y++) {
    const hue = (yLocalToValue255(y) / 255) * 360;
    ctx.fillStyle = hsvToCssRgb(hue, 1, 1);
    ctx.fillRect(ARGB_BAR_X.r, y, ARGB_BAR_WIDTH, 1);
  }

  // S バー (G 列): 現在 H, V を固定して S 0..1 のグラデーション.
  const hueDeg = (valueH / 255) * 360;
  const vNorm = valueV / 255;
  for (let y = 0; y < ARGB_BAR_AREA_HEIGHT; y++) {
    const sat = yLocalToValue255(y) / 255;
    ctx.fillStyle = hsvToCssRgb(hueDeg, sat, vNorm);
    ctx.fillRect(ARGB_BAR_X.g, y, ARGB_BAR_WIDTH, 1);
  }

  // V バー (B 列): 現在 H, S を固定して V 0..1 のグラデーション (= y=下 → 黒).
  const sNorm = valueS / 255;
  for (let y = 0; y < ARGB_BAR_AREA_HEIGHT; y++) {
    const val = yLocalToValue255(y) / 255;
    ctx.fillStyle = hsvToCssRgb(hueDeg, sNorm, val);
    ctx.fillRect(ARGB_BAR_X.b, y, ARGB_BAR_WIDTH, 1);
  }

  // 現在値マーカー: H/S/V 各バーに 2px 横線 (黒 1px + 白 1px) を上から重ねる.
  // 旧 `drawArgbBars` は ARGB 単色の塗りゲージ (= 下から value/4 px 塗る) で
  // 現在値が視覚化されるが、HSB はグラデーション全塗りなのでドラッグしても
  // バーが "動かない" ように見える. 現在値の y 位置 (= ARGB_BAR_AREA_HEIGHT - v/4)
  // に 1px 黒 + 1px 白 の縁取り横線を追加して視認性を確保 (= グラデ背景の
  // どんな色 (鮮やか/淡い/暗) でも片側は必ず映える). 白線が値の正確な位置を指す
  // (= yMarker 行に白、yMarker-1 行に黒の縁取り).
  const drawHsbMarker = (ch: 'r' | 'g' | 'b', value: number): void => {
    const v = Math.max(0, Math.min(255, value));
    const yMarker = ARGB_BAR_AREA_HEIGHT - Math.floor(v / 4);
    const x = ARGB_BAR_X[ch];
    ctx.fillStyle = '#000000';
    ctx.fillRect(x, Math.max(0, yMarker - 1), ARGB_BAR_WIDTH, 1);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x, Math.min(ARGB_BAR_AREA_HEIGHT - 1, yMarker), ARGB_BAR_WIDTH, 1);
  };
  drawHsbMarker('r', valueH);
  drawHsbMarker('g', valueS);
  drawHsbMarker('b', valueV);
};
